import React, { Component } from "react";
import styled from "styled-components/macro";
import { createId, createDataTestId } from "./utils/helpers";
import { mediaQueries } from "../styles/media.js";
import Question, { Optional } from "./Question";
import Fieldset from "./Fieldset";
import Legend from "./Legend";
import Hint from "./Hint";
import ErrorMessage from "./ErrorMessage";

const Checkbox = styled.div`
  position: relative;
  margin-bottom: 8px;
  padding-left: 2em;
  min-height: 32px;

  font-size: var(--juno-font-size-body-sm);
  line-height: normal;
  ${mediaQueries.md} {
    font-size: var(--juno-font-size-body-md);
  }

  input {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 40px;
    height: 40px;
    cursor: pointer;
    margin: 0;
    opacity: 0;
  }

  label {
    display: block;
    padding: 0.25em 0.75em;
    border: 2px solid transparent;
    cursor: pointer;
    touch-action: manipulation;
  }

  label::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 2em;
    height: 2em;
    border: 2px solid var(--juno-color-border);
    background: transparent;
  }

  label::after {
    content: "";
    width: 1em;
    height: 0.55em;
    position: absolute;
    top: 0.6em;
    left: 0.5em;
    transform: rotate(-45deg);
    border: solid;
    border-width: 0 0 5px 5px;
    border-color: #a7b1be;
    border-top-color: transparent;
    opacity: 0;
    background: transparent;
  }
  input:focus-visible + label::before {
    outline: var(--juno-focus-outline-width) solid var(--juno-color-focus);
    outline-offset: var(--juno-focus-outline-offset);
  }

  input:checked + label::after {
    opacity: 1;
  }

  input:disabled,
  input:disabled + label {
    cursor: default;
  }

  input:disabled + label {
    opacity: 0.5;
  }

  input:disabled:checked + label:after,
  input:disabled:checked + label:before {
    display: none;
  }
`;

// Safely converts input to a Map of checked items. Validates type before
// passing to Map constructor because the component can receive stale state
// values (boolean, string, object) during re-mounts, which would throw
// "TypeError: selected is not iterable" if passed directly to new Map().
function toCheckedItemsMap(selected) {
  if (selected instanceof Map) {
    return selected;
  }
  if (Array.isArray(selected)) {
    return new Map(selected);
  }
  return new Map();
}

class CheckboxQuestion extends Component {
  constructor(props) {
    super(props);

    this.state = {
      checkedItems: toCheckedItemsMap(this.props.selected),
    };
  }

  handleChange = (event) => {
    const item = event.target.name;
    const isChecked = event.target.checked;
    this.setState((prevState) => ({
      checkedItems: prevState.checkedItems.set(item, isChecked),
    }));
  };

  render() {
    return (
      <Question>
        <Fieldset>
          <Legend>
            {this.props.question} {this.props.optional ? <Optional /> : null}
            {this.props.hint ? <Hint>{this.props.hint}</Hint> : null}
            {this.props.validationError ? <ErrorMessage>{this.props.validationError}</ErrorMessage> : null}
          </Legend>
          {this.props.options.map((option, i) => {
            const componentId = createId("input", `${option.title}`);

            const dataTestId = createDataTestId("checkbox", `${option.title}`);

            return (
              <Checkbox option={option} key={i} {...this.props}>
                <input
                  type="checkbox"
                  name={this.props.name ? this.props.name : option.name}
                  title={option.title}
                  checked={!!this.state.checkedItems.get(this.props.name ? this.props.name : option.name)}
                  onChange={this.handleChange}
                  onClick={this.props.validate}
                  id={componentId}
                  data-testid={dataTestId}
                />
                <label htmlFor={componentId}>{option.title}</label>
              </Checkbox>
            );
          })}
        </Fieldset>
      </Question>
    );
  }
}

export default CheckboxQuestion;
